import React, { useEffect } from "react";
import { Star, ClipboardList, ShieldCheck } from "lucide-react";

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("sr-visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".sr").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const Leadership = () => {
  useScrollReveal();

  const leaders = [
    {
      name: "MP Igembe South",
      role: "Patron",
      description: "Oversees bursary allocation and ensures fair distribution.",
      icon: <Star size={28} />,
      color: "bg-blue-700",
      initials: "MP",
      delay: "0.1s",
    },
    {
      name: "Ward Bursary Officer",
      role: "Coordinator",
      description: "Manages student applications and coordinates verification.",
      icon: <ClipboardList size={28} />,
      color: "bg-primary",
      initials: "WO",
      delay: "0.2s",
    },
    {
      name: "CDF Admin",
      role: "Supervisor",
      description: "Monitors application audit trails and approves disbursements.",
      icon: <ShieldCheck size={28} />,
      color: "bg-blue-900",
      initials: "CA",
      delay: "0.3s",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-6xl mx-auto px-6">

        {/* Title */}
        <div className="sr text-center mb-14" style={{ transitionDelay: "0s" }}>
          <h1 className="text-4xl font-bold mb-4">Leadership</h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Meet the key officials who oversee the Igembe South Constituency Bursary programme.
          </p>
          <div className="w-24 h-1 bg-blue-700 mx-auto rounded-full mt-6" />
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {leaders.map((person, idx) => (
            <div
              key={idx}
              className="sr group bg-white rounded-2xl shadow-md p-8 text-center border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300 relative overflow-hidden flex flex-col items-center"
              style={{ transitionDelay: person.delay }}
            >
              {/* ripple */}
              <div className="absolute inset-0 bg-blue-50 scale-0 group-hover:scale-100 transition-transform duration-500 rounded-2xl origin-center" />

              {/* Avatar */}
              <div
                className={`relative z-10 ${person.color} text-white w-20 h-20 rounded-full flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}
              >
                <span className="text-xl font-bold">{person.initials}</span>
              </div>

              {/* Role badge */}
              <span className="relative z-10 inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
                {person.role}
              </span>

              <h3 className="relative z-10 font-semibold text-lg mb-2 text-gray-800">{person.name}</h3>
              <p className="relative z-10 text-gray-500 text-sm leading-relaxed">{person.description}</p>

              {/* Bottom icon */}
              <div className="relative z-10 mt-5 text-blue-700 opacity-20 group-hover:opacity-60 transition-opacity duration-300">
                {person.icon}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .sr { opacity: 0; transform: translateY(24px); transition: opacity 0.55s ease, transform 0.55s ease; }
        .sr-visible { opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default Leadership;